import { useState } from "react";
import { trackEvent } from "@/lib/analytics";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Closing";
import { submitPartnershipRequest } from "@/lib/partnership-leads.functions";
import heroImg from "@/assets/cc_main_hero.png";
import img2 from "@/assets/cc_img2_nfl.png";
import img3 from "@/assets/cc_img3_basketball.png";
import img4 from "@/assets/cc_img4_baseball.png";
import img6 from "@/assets/cc_img6_college_football.png";

export const Route = createFileRoute("/partnerships")({
  head: () => ({
    meta: [
      { title: "Partnerships & Sponsorships | ClutchCube" },
      {
        name: "description",
        content:
          "Partner with ClutchCube — the world's first immersive sports simulation chamber. Sponsorships, licensing, franchise development, and strategic partnership opportunities.",
      },
      { property: "og:title", content: "Partnerships & Sponsorships | ClutchCube" },
      {
        property: "og:description",
        content:
          "Partner with ClutchCube — sponsorships, licensing, franchise development, and strategic partnership opportunities in immersive sports entertainment.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://clutchcube.com/partnerships" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://clutchcube.com/partnerships" }],
  }),
  component: PartnershipsPage,
});

const benefits = [
  "Immersive brand experiences",
  "Fan engagement",
  "Experiential marketing",
  "Premium hospitality",
  "Customer acquisition",
  "Digital content creation",
  "Sponsorship activation",
  "Destination entertainment",
  "Recurring customer engagement",
];

const categories: { title: string; items: string[] }[] = [
  {
    title: "Professional Sports Teams",
    items: [
      "Official team experiences",
      "Licensed moments",
      "Fan activation",
      "Stadium district locations",
    ],
  },
  {
    title: "Colleges & Universities",
    items: [
      "Athletic partnerships",
      "Alumni engagement",
      "Campus entertainment",
      "Recruiting experiences",
    ],
  },
  {
    title: "Sports Equipment Companies",
    items: [
      "Official equipment",
      "Product demonstrations",
      "Sponsored challenges",
      "Retail integrations",
    ],
  },
  {
    title: "Athletic Apparel Brands",
    items: [
      "Official apparel",
      "Limited-edition merchandise",
      "Co-branded experiences",
      "Retail sales",
    ],
  },
  {
    title: "Beverage & Food Partners",
    items: [
      "Venue partnerships",
      "Branded experiences",
      "Hospitality",
      "Exclusive offerings",
    ],
  },
  {
    title: "Technology Partners",
    items: [
      "LED systems",
      "Audio",
      "Tracking technology",
      "AI & interactive software",
      "Cloud infrastructure",
    ],
  },
  {
    title: "Resorts & Casinos",
    items: [
      "Destination entertainment",
      "Guest experiences",
      "Tourism",
      "Hospitality",
    ],
  },
  {
    title: "Retail & Entertainment Developers",
    items: [
      "Lifestyle centers",
      "Mixed-use developments",
      "Mall redevelopment",
      "Entertainment districts",
    ],
  },
  {
    title: "Franchise Development Partners",
    items: [
      "Regional developers",
      "Master franchise groups",
      "International expansion",
    ],
  },
  {
    title: "Strategic Investors",
    items: [
      "Growth capital",
      "Industry expertise",
      "Distribution",
      "Strategic introductions",
    ],
  },
];

const sponsorships = [
  "Presenting Sponsor",
  "Official Equipment Partner",
  "Official Apparel Partner",
  "Official Technology Partner",
  "Official Beverage Partner",
  "Official Automotive Partner",
  "Official Financial Partner",
  "Official Airline Partner",
  "Official Media Partner",
];

const benefitFlow = [
  "Brand Exposure",
  "Fan Engagement",
  "Social Sharing",
  "Premium Experiences",
  "Content Creation",
  "Long-Term Loyalty",
];

const timeline = [
  "Discovery",
  "Strategy Meeting",
  "Concept Development",
  "Partnership Agreement",
  "Launch",
  "Growth",
];

const faqs: { q: string; a: string }[] = [
  {
    q: "Can ClutchCube create team-specific experiences?",
    a: "Yes. We work directly with professional teams and universities to design official, team-branded immersive moments that live inside ClutchCube venues and licensed activations.",
  },
  {
    q: "Can we sponsor a challenge?",
    a: "Absolutely. Individual moments and challenges are available for presenting, category-exclusive, and activation-based sponsorship packages.",
  },
  {
    q: "Can we license our brand?",
    a: "ClutchCube offers licensing structures for brands, teams, apparel, equipment, and media properties that align with our immersive sports category.",
  },
  {
    q: "Can we develop a ClutchCube location?",
    a: "Yes. Regional developers, master franchise groups, and mixed-use developers can apply through our Franchise Development track.",
  },
  {
    q: "Can we become a technology partner?",
    a: "We actively partner with LED, audio, tracking, AI, and cloud infrastructure companies powering the ClutchCube platform.",
  },
  {
    q: "How do sponsorships work?",
    a: "Sponsorships are structured across presenting, category-exclusive, and moment-level tiers, with venue, digital, and activation rights defined per partner.",
  },
];

const CATEGORY_OPTIONS = [
  "Professional Sports Team",
  "College Athletics",
  "Sports Equipment",
  "Apparel",
  "Technology",
  "Hospitality",
  "Casino",
  "Resort",
  "Retail Development",
  "Corporate Sponsor",
  "Strategic Investor",
  "Franchise Development",
  "Media",
  "Other",
];

const INTEREST_OPTIONS = [
  "Sponsorship",
  "Licensing",
  "Venue Partnership",
  "Technology",
  "Investment",
  "Franchise",
  "Marketing",
  "Other",
];

function PartnershipsPage() {
  const submit = useServerFn(submitPartnershipRequest);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [interests, setInterests] = useState<string[]>([]);
  const [started, setStarted] = useState(false);

  const onFirstInteraction = () => {
    if (started) return;
    setStarted(true);
    trackEvent("partnership_form_start", { form: "partnerships" });
  };

  const toggleInterest = (v: string) =>
    setInterests((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg(null);
    trackEvent("partnership_form_submit", { form: "partnerships" });
    const fd = new FormData(e.currentTarget);
    try {
      const res = await submit({
        data: {
          company: String(fd.get("company") || ""),
          contact_name: String(fd.get("contact_name") || ""),
          title: (fd.get("title") as string) || null,
          email: String(fd.get("email") || ""),
          phone: (fd.get("phone") as string) || null,
          website_url: (fd.get("website_url") as string) || null,
          category: String(fd.get("category") || "") as never,
          organization_size: (fd.get("organization_size") as string) || null,
          message: (fd.get("message") as string) || null,
          budget_range: (fd.get("budget_range") as string) || null,
          timeline: (fd.get("timeline") as string) || null,
          interests: interests as never,
          website: (fd.get("website") as string) || null,
          source: "partnerships-page",
        },
      });
      if (res.ok) {
        trackEvent("partnership_request_success", { form: "partnerships" });
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(("error" in res && res.error) || "Something went wrong.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg((err as Error).message);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <main>
        {/* HERO */}
        <section className="relative pt-32 pb-24 overflow-hidden border-b border-border">
          <img
            src={heroImg}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/85 to-background" />
          <div className="absolute inset-0 led-grid opacity-30" />
          <div className="relative mx-auto max-w-5xl px-6">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6">
              Partnerships & Sponsorships
            </p>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
              Partner with the future of{" "}
              <span className="text-gradient-ember">sports entertainment.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-muted-foreground max-w-3xl leading-relaxed">
              ClutchCube is building the world&rsquo;s first immersive sports simulation
              chamber, creating new opportunities for brands, teams, venues, sponsors, and
              strategic partners to participate in the rapidly growing Experience Economy.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#package"
                className="inline-flex items-center px-10 py-5 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition"
              >
                Request Partnership Package
              </a>
              <a
                href="#partner-form"
                onClick={() => trackEvent("partnership_cta_click", { placement: "partnerships_page" })}
                className="inline-flex items-center px-10 py-5 border border-border/70 text-foreground font-semibold uppercase tracking-widest text-sm rounded-sm hover:border-primary/60 hover:bg-card/60 transition"
              >
                Schedule a Conversation
              </a>
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <div className="absolute inset-0 led-grid opacity-10" />
          <div className="relative mx-auto max-w-6xl px-6">
            <div className="max-w-3xl mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Why Partner With ClutchCube
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                A new platform for fan engagement.
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                ClutchCube creates opportunities for organizations to connect with fans in ways
                traditional advertising cannot &mdash; through immersive, in-person moments that
                fans choose to step inside.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((b, i) => (
                <div
                  key={b}
                  className="border border-border/70 bg-card/40 p-6 rounded-sm hover:border-primary/40 transition"
                >
                  <p className="text-[11px] uppercase tracking-[0.25em] text-primary">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="mt-3 font-display text-lg text-foreground leading-snug">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PARTNERSHIP CATEGORIES */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <img
            src={img2}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
          <div className="relative mx-auto max-w-6xl px-6">
            <div className="max-w-3xl mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Partnership Categories
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Ten strategic tracks. One immersive platform.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((c, i) => (
                <div
                  key={c.title}
                  className="border border-border/70 bg-card/60 p-6 rounded-sm hover:border-primary/50 transition flex flex-col"
                >
                  <p className="text-[11px] uppercase tracking-[0.25em] text-primary">
                    Track {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-3 font-display text-xl text-foreground leading-snug">
                    {c.title}
                  </h3>
                  <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                    {c.items.map((it) => (
                      <li key={it} className="flex gap-2">
                        <span className="text-primary">&rsaquo;</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SPONSORSHIP OPPORTUNITIES */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <img
            src={img3}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
          <div className="relative mx-auto max-w-6xl px-6 grid lg:grid-cols-[1fr_1.2fr] gap-16 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Sponsorship Opportunities
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Sponsor iconic moments.
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Sponsors become part of unforgettable sports experiences instead of traditional
                advertisements &mdash; woven into the moments themselves.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {sponsorships.map((s) => (
                <div
                  key={s}
                  className="border border-border/70 bg-card/60 px-5 py-4 rounded-sm text-foreground text-sm"
                >
                  {s}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BENEFITS FLOW */}
        <section className="relative py-24 border-b border-border">
          <div className="mx-auto max-w-5xl px-6">
            <div className="max-w-3xl mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Partnership Benefits
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                From exposure to loyalty.
              </h2>
            </div>
            <div className="grid gap-3">
              {benefitFlow.map((step, i) => (
                <div key={step} className="flex flex-col items-center">
                  <div className="w-full max-w-md border border-primary/30 bg-card/60 rounded-sm py-5 px-6 text-center">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-primary">
                      Step {String(i + 1).padStart(2, "0")}
                    </p>
                    <p className="mt-2 font-display text-xl text-foreground">{step}</p>
                  </div>
                  {i < benefitFlow.length - 1 && (
                    <div className="h-8 w-px bg-primary/40 my-1" aria-hidden />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DOWNLOAD PACKAGE */}
        <section
          id="package"
          className="relative py-28 border-b border-border overflow-hidden"
        >
          <img
            src={img6}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/85 to-background" />
          <div className="absolute inset-0 led-grid opacity-30" />
          <div className="relative mx-auto max-w-4xl px-6 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
              Partnership & Sponsorship Package
            </p>
            <h2 className="font-display text-4xl sm:text-6xl leading-[1.05]">
              Explore partnership opportunities.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Download the complete Partnership & Sponsorship Package to learn about available
              opportunities, activation concepts, venue integrations, sponsorship categories,
              and future expansion plans.
            </p>
            <div className="mt-10">
              <a
                href="#partner-form"
                onClick={() => trackEvent("partnership_cta_click", { placement: "partnerships_page" })}
                className="inline-flex items-center px-10 py-5 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition"
              >
                Request Partnership Package
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Delivered via secure private link · Not publicly downloadable
            </p>
          </div>
        </section>

        {/* PARTNERSHIP FORM */}
        <section id="partner-form" className="relative py-24 border-b border-border">
          <div className="mx-auto max-w-4xl px-6">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Partnership Request
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Start the conversation.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Tell us about your organization and how you&rsquo;d like to partner with
                ClutchCube. Our team will follow up within two business days with a secure link
                to the Partnership & Sponsorship Package.
              </p>
            </div>

            {status === "success" ? (
              <div className="border border-primary/40 bg-card/60 p-8 rounded-sm">
                <p className="font-display text-2xl text-foreground">Thank you.</p>
                <p className="mt-3 text-muted-foreground">
                  Your partnership request has been received. A secure link to the Partnership
                  & Sponsorship Package has been sent to the email you provided. Our team will
                  reach out shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} onFocus={onFirstInteraction} onChange={onFirstInteraction} className="grid sm:grid-cols-2 gap-5">
                {/* honeypot */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden
                />
                <Field label="Company Name" name="company" required />
                <Field label="Primary Contact" name="contact_name" required />
                <Field label="Title" name="title" />
                <Field label="Email" name="email" type="email" required />
                <Field label="Phone" name="phone" type="tel" />
                <Field label="Website" name="website_url" type="url" />
                <div>
                  <Label>Partnership Category *</Label>
                  <select
                    name="category"
                    required
                    defaultValue=""
                    className="w-full bg-transparent border border-border/70 rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary/60"
                  >
                    <option value="" disabled className="bg-background">
                      Select a category
                    </option>
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c} value={c} className="bg-background">
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <Field label="Organization Size" name="organization_size" />
                <Field label="Budget Range (optional)" name="budget_range" />
                <Field label="Desired Timeline" name="timeline" />
                <div className="sm:col-span-2">
                  <Label>Interested In</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {INTEREST_OPTIONS.map((opt) => {
                      const active = interests.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggleInterest(opt)}
                          className={`px-4 py-2 text-sm rounded-sm border transition ${
                            active
                              ? "border-primary bg-primary/15 text-foreground"
                              : "border-border/70 bg-card/40 text-muted-foreground hover:border-primary/40"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>Message</Label>
                  <textarea
                    name="message"
                    rows={5}
                    className="w-full bg-transparent border border-border/70 rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary/60"
                  />
                </div>
                {status === "error" && (
                  <div className="sm:col-span-2 border border-destructive/60 bg-destructive/10 text-destructive-foreground px-4 py-3 rounded-sm text-sm">
                    {errorMsg || "Something went wrong. Please try again."}
                  </div>
                )}
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center px-10 py-4 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? "Sending…" : "Request Partnership Information"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* PARTNER SUCCESS TIMELINE */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <img
            src={img4}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
          <div className="relative mx-auto max-w-6xl px-6">
            <div className="max-w-3xl mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Partner Success Timeline
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                From first conversation to sustained growth.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
              {timeline.map((t, i) => (
                <div
                  key={t}
                  className="border border-border/70 bg-card/50 rounded-sm p-5 relative"
                >
                  <p className="text-[11px] uppercase tracking-[0.3em] text-primary">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="mt-2 font-display text-lg text-foreground leading-snug">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-24 border-b border-border">
          <div className="mx-auto max-w-4xl px-6">
            <div className="max-w-2xl mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">FAQ</p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Common partner questions.
              </h2>
            </div>
            <div className="divide-y divide-border/60 border-y border-border/60">
              {faqs.map((f) => (
                <details key={f.q} className="group py-5">
                  <summary className="flex items-center justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg text-foreground">{f.q}</span>
                    <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING */}
        <section className="relative py-32 overflow-hidden">
          <img
            src={heroImg}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
          <div className="absolute inset-0 led-grid opacity-25" />
          <div className="relative mx-auto max-w-5xl px-6 text-center">
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.98]">
              Let&rsquo;s build the future of sports entertainment{" "}
              <span className="text-gradient-ember">together.</span>
            </h2>
            <p className="mt-8 text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Whether you&rsquo;re a professional sports organization, global brand, technology
              company, or franchise developer, we&rsquo;re seeking visionary partners to help
              shape the next generation of immersive sports entertainment.
            </p>
            <div className="mt-10">
              <a
                href="#partner-form"
                onClick={() => trackEvent("partnership_cta_click", { placement: "partnerships_page" })}
                className="inline-flex items-center px-10 py-5 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition"
              >
                Request Partnership Package
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
      {children}
    </label>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      <input
        name={name}
        type={type}
        required={required}
        className="w-full bg-transparent border border-border/70 rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary/60"
      />
    </div>
  );
}
